import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash-es';
import { hasPermission, Permission, Role } from 'zl-shared/dist/auth';

import rules from '@/components/Authorize/rules';
import { Feature } from '@/queries/products/types';

import { AppState } from '../helper';

const SPECIAL_ROLES = new Set([Role.PARTNER, Role.HOST, Role.ADMIN, Role.MODERATOR]);

export const selectIsSpecialRole = createSelector(
  (state: AppState) => state.user.role,
  (role) => SPECIAL_ROLES.has(role as Role),
);

export const selectIsAdmin = createSelector(
  (state: AppState) => state.user.role,
  (role) => role === Role.ADMIN,
);

export const selectIsFreeUser = createSelector(
  (state: AppState) => state.user.role,
  (state: AppState) => state.user.subscription?.activeProductKeys,
  (role, activeProductKeys) => {
    if (SPECIAL_ROLES.has(role as Role)) {
      return false;
    }
    return !activeProductKeys?.length;
  },
);

const createSelectIsUserHasFeature = (feature: Feature, roleCheck: (state: AppState) => boolean) =>
  createSelector(
    roleCheck,
    (state: AppState) => state.user.subscription?.featuresUntil,
    (canAccess, featuresUntil) =>
      canAccess || !!(featuresUntil?.[feature] && Date.parse(featuresUntil[feature]) > Date.now()),
  );

export const selectHasExplorerFeature = createSelectIsUserHasFeature('explorer', selectIsSpecialRole);
export const selectHasEducatorFeature = createSelectIsUserHasFeature('educator', selectIsSpecialRole);
export const selectHasVipFeature = createSelectIsUserHasFeature('vip', selectIsAdmin);
export const selectHasGuidedExperienceFeature = createSelectIsUserHasFeature('guidedExperience', () => false);
export const selectHasSingletonFeature = createSelectIsUserHasFeature('singleton', selectIsSpecialRole);

export const createSelectCanAccessHabitat = _.memoize((habitatId: string | undefined) =>
  habitatId
    ? createSelector(
        selectHasExplorerFeature,
        (state: AppState) => state.user.subscription?.habitatsAccessUntil,
        (hasExplorerFeature, habitatsAccessUntil) =>
          hasExplorerFeature ||
          !!(habitatsAccessUntil?.[habitatId] && Date.parse(habitatsAccessUntil[habitatId]) > Date.now()),
      )
    : () => false,
);

export const selectShouldShowAppOnboarding = createSelector(
  (state: AppState) => state.user.logged,
  (state: AppState) => state.user.isAppOnboarded,
  (state: AppState) => state.modals.appOnboardingModal,
  (logged, isAppOnboarded, modalState) => logged && !isAppOnboarded && !Object.values(modalState).every(Boolean),
);

export const selectCanDownloadMedia = (state: AppState) => !selectIsFreeUser(state);

export const selectCanDownloadMediaWithoutWatermark = createSelector(
  [(state: AppState) => state.user.logged, (state: AppState) => state.user.role, selectHasVipFeature],
  (logged, role, hasVipFeature) => {
    // active VIP users
    if (hasVipFeature) {
      return true;
    }
    if (!logged || [Role.USER, 'guest'].includes(role as string)) {
      return false;
    }
    const canEditAlbumMedia = hasPermission(Permission.Habitat.EditAlbumMedia, null, role as Role, rules);
    const canEditAlbumMediaDownload = hasPermission(Permission.Habitat.EditAlbumMediaDownload, null, role as Role, rules);
    return canEditAlbumMedia && canEditAlbumMediaDownload;
  },
);

export const selectCanAccessEducatorsApp = createSelector(
  selectHasEducatorFeature,
  (state: AppState) => state.user.persona,
  (hasEducatorFeature, persona) => {
    return hasEducatorFeature || persona === 'educator';
  },
);

export const selectIsProfileCompleted = createSelector(
  (state: AppState) => state.user.name,
  (state: AppState) => state.user.ageGroup,
  (state: AppState) => state.user.persona,
  (name, ageGroup, persona) => !!name && !!ageGroup && !!persona,
);

export const selectActiveProductNames = (state: AppState) => state.user.subscription?.activeProductNames;

export const selectDayPassHabitatsHistory = createSelector(
  (state: AppState) => state.user.subscription?.dayPassHabitatsHistory,
  (history) => new Set(history ?? []),
);

export const selectSupportedHabitatsHistory = createSelector(
  (state: AppState) => state.user.subscription?.supportedHabitatsHistory,
  (history) => new Set(history ?? []),
);

export const selectFavoriteHabitatIds = createSelector(
  (state: AppState) => state.user.favoriteHabitats,
  (favoriteHabitatIds) => new Set(favoriteHabitatIds ?? []),
);
